import React, { useState, useEffect, useRef } from 'react';

interface MonthYearPickerProps {
  value: string; // Format: "YYYY-MM"
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  error?: string;
  minDate?: string; // Format: "YYYY-MM"
  maxDate?: string; // Format: "YYYY-MM"
  disabled?: boolean;
}

export const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select month and year',
  error,
  minDate,
  maxDate,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  // const years = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i);

  useEffect(() => {
    if (value) {
      const [year, month] = value.split('-');
      setSelectedYear(parseInt(year));
      setSelectedMonth(parseInt(month) - 1);
    } else {
      setSelectedYear(currentYear);
      setSelectedMonth(null);
    }
  }, [value, currentYear]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMonthSelect = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    const formattedMonth = (monthIndex + 1).toString().padStart(2, '0');
    const newValue = `${selectedYear}-${formattedMonth}`;
    onChange(newValue);
    setIsOpen(false);
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (selectedMonth !== null) {
      const formattedMonth = (selectedMonth + 1).toString().padStart(2, '0');
      const newValue = `${year}-${formattedMonth}`;
      onChange(newValue);
    }
  };

  const formatDisplayValue = (value: string) => {
    if (!value) return placeholder;
    const [year, month] = value.split('-');
    const monthIndex = parseInt(month) - 1;
    return `${months[monthIndex]} ${year}`;
  };

  const isDateDisabled = (year: number, monthIndex: number) => {
    const dateString = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}`;
    
    if (minDate && dateString < minDate) return true;
    if (maxDate && dateString > maxDate) return true;
    
    return false;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={formatDisplayValue(value)}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          readOnly
          disabled={disabled}
          className={`w-full border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer bg-white ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`}
          placeholder={placeholder}
          aria-label={label}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Year Selector */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleYearChange(selectedYear - 1)}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!!(minDate && selectedYear <= parseInt(minDate.split('-')[0]))}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="font-semibold text-gray-900">{selectedYear}</span>
              <button
                onClick={() => handleYearChange(selectedYear + 1)}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!!(maxDate && selectedYear >= parseInt(maxDate.split('-')[0]))}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Months Grid */}
          <div className="p-3">
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, index) => {
                const isDisabled = isDateDisabled(selectedYear, index);
                const isSelected = selectedMonth === index && selectedYear === parseInt(value.split('-')[0]);
                
                return (
                  <button
                    key={index}
                    onClick={() => !isDisabled && handleMonthSelect(index)}
                    disabled={isDisabled}
                    className={`p-2 text-sm rounded transition-colors ${
                      isSelected 
                        ? 'bg-blue-500 text-white hover:bg-blue-600' 
                        : isDisabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-blue-50'
                    }`}
                  >
                    {month.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};
